// @flow
import React from 'react';
import { number, object, string } from 'yup';
import { getIn, setIn } from 'formik';
import { has, isEqual, isPlainObject, omitBy, sortBy } from 'lodash';
import { DeleteHeaderWithWarning } from '@presentational/DeleteHeaderWithWarning';
import { FRONT_END_DISPLAY_NAMES } from '@streaming-projects/enums';
import {
  ALL_IN_SMALL_CASE,
  DEDICATED_CLUSTER_TYPE,
  DEFAULT_VALUE_FOR_DB_ROW_ID,
  ENTERPRISE_CLUSTER_TYPE,
  FREIGHT_CLUSTER_TYPE,
  GLOBAL_DISCOUNT_APPLIED_BACKEND_NAME,
  MAX_INPUT_VALUE_FOR_ANY_FIELD,
  RATE_CARD_ID_FOR_4_11_2024,
  PROVIDED_BY_LABEL_TEXT_TO_TOOLTIP_TEXT_MAP,
  ALL,
  RATE_CARD_BACKEND_NAME,
} from '@src/constants';

import {
  ADVANCED_SECURITY_ENABLED_BACKEND_NAME,
  CUSTOM_DISCOUNTS_ENABLED_BACKEND_NAME,
  ESTIMATE_CUSTOMER_VIEW_ENABLED_BACKEND_NAME,
  GATEWAY_ADD_ON_ENABLED_BACKEND_NAME,
  SFDC_QUOTE_NAME_BACKEND_NAME,
} from '../constants';

import { StyledLabel } from './styledComponents';

export const convertToArrayIfNotArray = (value) => (Array.isArray(value) ? value : [value]);

export const getIfUserIsAdminAndCustomerViewIsDisabled = (user, estimate) => {
  return (
    getIfUserIsAdminBasedOnGivenDetails(user) &&
    getIfCustomerViewIsDisabledBasedOnGivenEstimateDetails(estimate)
  );
};

export const getIfUserIsAdminBasedOnGivenDetails = (user) => {
  return user?.admin === true;
};

export const getIfCustomerViewIsDisabledBasedOnGivenEstimateDetails = (estimate) =>
  estimate?.inputs?.[ESTIMATE_CUSTOMER_VIEW_ENABLED_BACKEND_NAME] === false;

export const getIfCustomerViewIsEnabled = (values) =>
  values?.[ESTIMATE_CUSTOMER_VIEW_ENABLED_BACKEND_NAME] === true;

export const getIfCustomDiscountsEnabled = (values) =>
  values?.[CUSTOM_DISCOUNTS_ENABLED_BACKEND_NAME] === true;

export const getIfAdvancedSecurityIsEnabled = (values) =>
  values?.[ADVANCED_SECURITY_ENABLED_BACKEND_NAME] === true;

export const getIfGatewayAddOnIsEnabled = (values) =>
  values?.[GATEWAY_ADD_ON_ENABLED_BACKEND_NAME] === true;

export const getIfQuoteLineIdIsProvided = (values) =>
  (values?.[SFDC_QUOTE_NAME_BACKEND_NAME] ?? '').length > 0;

export const getIfCustomDiscountsEnabledAndNotInCustomerView = (values) =>
  getIfCustomDiscountsEnabled(values) && !getIfCustomerViewIsEnabled(values);

export const getIfCustomDiscountsEnabledORInCustomerView = (values) =>
  getIfCustomDiscountsEnabled(values) || getIfCustomerViewIsEnabled(values);

export const getTotalCommitmentDollarsDiscountedForTheGivenYear = (estimate, year) => {
  const afterDiscountArray = estimate.annual_summaries.after_discount;
  const estimatedCommitObj = afterDiscountArray.filter(
    (x) => x.field === 'Estimated Commit (Discounted)'
  )[0];

  const yearlyValues = estimatedCommitObj.yearly_values;
  const selectedYearYearlyValues = yearlyValues.filter((x) => x.label === `Year ${year}`);

  // The only way in which the selectedYearYearlyValues array can be empty is when the year is out of range
  // In that case, we dont want this validation to run
  if (selectedYearYearlyValues.length > 0) {
    return Number(selectedYearYearlyValues[0].value);
  } else {
    return Number.POSITIVE_INFINITY;
  }
};

export const getFormattedRateCardVersion = (rateCardVersion) =>
  rateCardVersion ? rateCardVersion.replace('/', '_') : '';

export const getRateCardLabelByName = (rateCards, rateCardName) =>
  rateCards.find((rateCard) => rateCard.name === rateCardName).label;

export const getAccordianPanelDetailsObject = (title, component, key = null) => ({
  key: key != null ? key : title.toLowerCase().split(' ').join('-'),
  // When the user provides a title component, use it instead of the default one
  title: {
    content: typeof title === 'string' ? <StyledLabel basic={true} content={title} /> : title,
  },
  content: {
    content: component,
  },
});

export const getInitialValue = (col, inputSource, backendFieldName) => {
  const inputTransformationFunc = col?.inputTransformationFunc;
  const inputComingFromSource = inputSource[backendFieldName];
  let inputToUse = inputComingFromSource;
  if (inputTransformationFunc) {
    inputToUse = inputTransformationFunc(inputComingFromSource);
  }
  return inputToUse;
};

export const getValidation = (col, objectToBePassedInForValidation) => {
  const validationConfigured = col.validation;
  let validationToUse = validationConfigured;
  if (typeof validationConfigured === 'function') {
    validationToUse = validationConfigured(objectToBePassedInForValidation);
  }
  return validationToUse;
};

export const getFormikFormInputsFromColConfigAndInputSource = (
  columnsConfig,
  inputSource,
  objectToBePassedInForValidation
) => {
  const initialValues = {};
  const validationSchemaObj = {};
  const initialTouched = {};

  for (const col of columnsConfig) {
    const backendFieldName = col.backendFieldName;
    initialValues[backendFieldName] = getInitialValue(col, inputSource, backendFieldName);
    validationSchemaObj[backendFieldName] = getValidation(col, objectToBePassedInForValidation);
    if (col.initialTouched) {
      initialTouched[backendFieldName] = true;
    }
  }

  const validationSchema = object(validationSchemaObj);
  return { initialValues, initialTouched, validationSchema };
};

export const convertUTCTimeStampToHumanReadableTime = (updatedAt) => {
  return new Intl.DateTimeFormat('en-US', {
    timeStyle: 'long',
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(updatedAt));
};

/**
 * Converts a UTC date string to a human readable date string.
 * Example: "2026-03-12T00:00:00Z" -> "Mar 12, 2026"
 * @param {string} dateString - The UTC date string to convert.
 * @returns {string | null} The human readable date string or null if the date string is not provided.
 */
export const convertUTCDateToHumanReadableDate = (dateString) => {
  if (!dateString) {
    return null;
  }
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(new Date(dateString));
};

export const convertDateToMetaDataAPIFormat = (date) => {
  // THIS IS A HACK, FIX THIS
  // IDEALLY NO DATE FORMATTING SHOULD BE NEEDED AND JUST CALLING convertLocalToUTCDate
  // SHOULD HAVE BEEN SUFFICIENT
  // todo::SP Fix this hack
  const dateString = new Intl.DateTimeFormat('en-Gb', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(date);

  const parts = dateString.split('/');
  const [month, day, year] = parts;
  return `${year}-${day.padStart(2, '0')}-${month.padStart(2, '0')}T00:00:00Z`;
};

export const convertToISODateString = (date) => {
  // TODO::SP Fix this hack once backend accepts milliseconds in the ISO String
  return date.toISOString().replace('.000Z', 'Z');
};

export const getDropdownOptionsFromArray = (values, normalizeAll = false) => {
  return values.map((x) => {
    if (normalizeAll && x && x.toUpperCase() === ALL) {
      return { name: ALL_IN_SMALL_CASE, display_name: ALL };
    }
    return { name: x, display_name: x };
  });
};

export const getDropdownOptionsFromLabelAndValuesArray = (optionsArr) => {
  const optionsToUse = [];
  for (const option of optionsArr) {
    optionsToUse.push({
      name: option.value,
      display_name: option.label,
    });
  }
  return optionsToUse;
};

export const isFormEditedBasedOnCurrentValues = (values, initialValues, fieldsInForm) => {
  let isEdited = false;
  for (const field of fieldsInForm) {
    const backendFieldName = field.backendFieldName;
    const currentValue = getIn(values, backendFieldName);
    const originalValue = getIn(initialValues, backendFieldName);
    if (!isEqual(currentValue, originalValue)) {
      isEdited = true;
      break;
    }
  }
  return isEdited;
};

export const getArrayElemsExcludingGivenElem = (array, elemToExclude) =>
  array.filter((elem) => elem !== elemToExclude);

export const resetFormValuesToInitialValues = (values, fieldsInForm, initialValues, resetForm) => {
  const newValues = {
    ...values,
  };
  for (const field of fieldsInForm) {
    const backendFieldName = field.backendFieldName;
    // Limitation: This direct assignment does not support nested keys (e.g., 'ABC.DEF').
    // For nested keys, use resetFormValuesToInitialValuesUsingSetIn instead.
    newValues[backendFieldName] = getIn(initialValues, backendFieldName);
  }
  resetForm({ values: newValues });
};

export const resetFormValuesToInitialValuesUsingSetIn = (
  values,
  fieldsInForm,
  initialValues,
  resetForm
) => {
  let newValues = { ...values };

  for (const field of fieldsInForm) {
    const backendFieldName = field.backendFieldName;
    const initialValue = getIn(initialValues, backendFieldName);
    newValues = setIn(newValues, backendFieldName, initialValue);
  }

  resetForm({ values: newValues });
};

export const extractRelevantFieldValuesFromForm = (fieldsInForm, values) => {
  const toRet = {};

  for (const field of fieldsInForm) {
    const backendFieldName = field.backendFieldName;
    toRet[backendFieldName] = getIn(values, backendFieldName);

    const transformationFunctionToSendToBackEnd = field.transformationFunctionToSendToBackEnd;
    if (transformationFunctionToSendToBackEnd) {
      toRet[backendFieldName] = transformationFunctionToSendToBackEnd(toRet[backendFieldName]);
    }
  }

  return toRet;
};

export const getEditedFieldNamesBasedOnFormikValuesAndInitialValues = (
  values,
  initialValues,
  fieldsInForm
) => {
  const editedFields = [];
  for (const field of fieldsInForm) {
    const backendFieldName = field.backendFieldName;
    const currentValue = getIn(values, backendFieldName);
    const originalValue = getIn(initialValues, backendFieldName);
    if (!isEqual(currentValue, originalValue)) {
      editedFields.push(backendFieldName);
    }
  }
  return editedFields;
};

export const convertConfigArrayToFrontEndFormat = (configArray) => {
  const newConfigArray = [];

  for (const elem of configArray) {
    newConfigArray.push({
      ...elem,
      isRowFrozen: true,
      isRowOpenedForEditing: false,
      // todo::Fix this and move to SG because other usages dont need this
      rules_count: elem?.rules_count ?? 0,
    });
  }

  return newConfigArray;
};

export const getIfArrayContainDuplicates = (arr) => {
  return new Set(arr).size !== arr.length;
};

export const convertUTCToLocalDate = (date) => {
  if (!date) {
    return date;
  }
  let toRet = date;
  toRet = new Date(toRet);
  toRet = new Date(toRet.getUTCFullYear(), toRet.getUTCMonth(), toRet.getUTCDate());
  return toRet;
};

export const convertLocalToUTCDate = (date) => {
  if (!date) {
    return date;
  }
  let toRet = date;
  toRet = new Date(toRet);
  toRet = new Date(Date.UTC(toRet.getFullYear(), toRet.getMonth(), toRet.getDate()));
  return toRet;
};

const getObjectFromLabelAndValues = (row) => {
  const objectToRet = {};
  for (const [_, value] of Object.entries(row)) {
    objectToRet[value.label] = value.value;
  }
  return objectToRet;
};

const getSummaryRowObjectFromValues = (oldValuesArray) => {
  let isMultiLevelValue = false;

  oldValuesArray.forEach((row) => {
    if (Object.prototype.hasOwnProperty.call(row, 'label')) {
      isMultiLevelValue = true;
    }
  });

  let inputDataSourceFormatted;

  if (!isMultiLevelValue) {
    const toRet = [];
    oldValuesArray.forEach((row) => {
      const thisRowObj = getObjectFromLabelAndValues(row);
      toRet.push(thisRowObj);
    });
    inputDataSourceFormatted = toRet;
  } else {
    const toRet = {};
    oldValuesArray.forEach((row) => {
      const key = row.label;
      toRet[key] = row.values.map((x) => getObjectFromLabelAndValues(x));
    });
    inputDataSourceFormatted = toRet;
  }

  return inputDataSourceFormatted;
};

export const getPlainObjectFromBackEndSpendSummaryFormat = (
  inputDataSource,
  orderingOfRows = []
) => {
  const inputDataSourceFormatted = {};

  for (const value of Object.values(inputDataSource)) {
    const newKey = value.label;
    const oldValues = value.values;
    inputDataSourceFormatted[newKey] = getSummaryRowObjectFromValues(oldValues);
  }

  if (orderingOfRows.length > 0) {
    const inputDataSourceFormattedAndOrdered = {};
    for (const key of orderingOfRows) {
      // A section can be in the desired ordering but absent from the backend response
      // when there is no usage for it (e.g. the backend omits the RTCE section when its
      // usage is zero and not using DUST yet). Skip it rather than assigning undefined,
      // which the grid builder cannot render.
      if (!isKeyPresentInObject(inputDataSourceFormatted, key)) {
        continue;
      }
      inputDataSourceFormattedAndOrdered[key] = inputDataSourceFormatted[key];
    }
    return inputDataSourceFormattedAndOrdered;
  }

  return inputDataSourceFormatted;
};

// todo::Implement Input/Output Transformation for all other areas too!

export const getFormattedRegionsUniverse = (dataUniverse, key = 'provider') => {
  const dataUniverseFormatted = [];

  dataUniverse.forEach((providerWithRegions) => {
    const provider = providerWithRegions[key];
    const regions = providerWithRegions.region;
    regions.forEach((regionObj) => {
      dataUniverseFormatted.push({
        provider,
        region: regionObj.region_name,
        region_label: regionObj.region_label,
      });
    });
  });

  return dataUniverseFormatted;
};

// AWS GovCloud region names use the `us-gov-` prefix (e.g. us-gov-east-1, us-gov-west-1). There is
// no backend flag for GovCloud today, so identify GovCloud regions by this naming convention.
const isGovCloudRegion = (regionName) => regionName.toLowerCase().startsWith('us-gov-');

// Disables (does NOT remove) GovCloud regions in a dropdown's options: they can't be newly
// selected, but an estimate that already has a GovCloud region keeps its option in the list so it
// still displays and validates. `name` holds the raw region value, so it matches across the
// cluster, connector, Flink and Stream Governance region dropdowns alike.
export const markGovCloudRegionsAsNotSelectable = (options) =>
  options.map((option) =>
    isGovCloudRegion(option.name) ? { ...option, shouldNotBeSelectable: true } : option
  );

export const markOptionsThatAreAlreadySelectedAsNotSelectable = (
  options,
  alreadySelectedValues
) => {
  return options.map((option) => ({
    ...option,
    shouldNotBeSelectable: alreadySelectedValues.includes(option.name),
  }));
};

export const convertArrayOfValueLabelsToObject = (arrayOfValueLabels) => {
  const toRet = {};

  for (const elem of arrayOfValueLabels) {
    toRet[elem.label] = elem.value;
  }
  return toRet;
};

export const convertObjectToContainKeysInCapitals = (obj) => {
  const objWithKeysInCapitals = {};
  for (const [originalKey, originalValue] of Object.entries(obj)) {
    let valueToUse = originalValue;

    // Recursively convert the value
    if (isPlainObject(originalValue)) {
      valueToUse = convertObjectToContainKeysInCapitals(originalValue);
    }

    objWithKeysInCapitals[originalKey.toUpperCase()] = valueToUse;
  }
  return objWithKeysInCapitals;
};

export const getPercentageValidation = (label, max = 100) =>
  number().label(label).typeError('You must specify a number').min(0).max(max).required();

export const getDeleteWithWarningHeaderAndBody = (objectType, objectName) => {
  const header = `Delete ${objectType} ${objectName}`;
  const headerBody = <DeleteHeaderWithWarning objectType={objectType} />;
  return { header, headerBody };
};

export const shouldSkipDataFetching = (params) => {
  // Check if any parameter value is falsy
  for (const key in params) {
    if (!params[key]) {
      return true; // Skip if any parameter is falsy
    }
  }
  return false; // Do not skip if all parameters are truthy
};

export const getNumberOfYearsBasedOnMonths = (monthsCount) => {
  return Math.ceil(monthsCount / 12);
};

export const handleGridRowSelectionChanged = (setSingleRowFunc, clearSelectionFunc) => (event) => {
  const selectedNodes = event.api.getSelectedNodes();
  if (selectedNodes.length === 1) {
    setSingleRowFunc(selectedNodes[0].data);
  } else {
    clearSelectionFunc();
  }
};

// CTODO::SP Add Quote ID Validation in Estimator 2.0
export const sfdcQuoteIdValidation = string().matches(new RegExp('^Q-[0-9]+$'), {
  excludeEmptyString: true,
  message:
    'Quote Name, if provided, should start with "Q-" and should at least contain one digit. Post that should only contain digits',
});

export const sfdcOpportunityIdValidation = string()
  .matches(new RegExp('^006[a-zA-Z0-9]*$'), {
    message:
      'Invalid Opportunity ID format. Opportunity ID, if provided, should start with "006" and must be of length 18.',
  })
  .length(18, 'Opportunity ID has to be of length 18');

// CTODO::SP Add Commit Amount Validation to Default Commit
export const commitAmountValidation = number()
  .typeError('Total Commit must be a number')
  .label(FRONT_END_DISPLAY_NAMES.COMMIT_AMOUNT)
  .min(0)
  .max(MAX_INPUT_VALUE_FOR_ANY_FIELD);

export const fetchSingleSelectedRateCardOptionFromGivenRCs = (
  selectedRateCardName,
  universeOfAllRCs
) => {
  const currentRateCard = universeOfAllRCs.find(
    (rateCard) => rateCard.name === selectedRateCardName
  );
  if (!currentRateCard) {
    throw new Error(`Rate Card with name ${selectedRateCardName} not found.`);
  }
  return [
    {
      name: currentRateCard.name,
      display_name: currentRateCard.label,
      shouldNotBeSelectable: !currentRateCard.actionable,
    },
  ];
};

export const getSortedFieldValuesFromArrayOfObjects = (arrayOfObjects, field) => {
  return sortBy(arrayOfObjects, field).map((item) => item[field]);
};

export const getAccordionsKeyFromPanelsArray = (panels) => {
  return panels.map((panel) => panel.key).join(',');
};

export const pickKeysFromObject = (obj, keys) => {
  return Object.fromEntries(keys.filter((key) => key in obj).map((key) => [key, obj[key]]));
};

export const getValuesOfGivenKeysFromObjectAsArray = (obj, keys) => {
  return keys.map((key) => obj[key]);
};

/**
 * Groups an array of objects by a specified key.
 * Returns an object where each key is a unique value from the specified key,
 * and each value is an array of objects that share that key value.
 *
 * @param {Array<Object>} array - The array of objects to group
 * @param {string} groupKey - The key to group objects by
 * @returns {Object<string, Array<Object>>} - An object with keys as unique values from groupKey and values as arrays of grouped objects
 *
 * @example
 * const items = [
 *   { type: 'fruit', name: 'apple' },
 *   { type: 'fruit', name: 'banana' },
 *   { type: 'vegetable', name: 'carrot' }
 * ];
 * groupArrayOfObjectsByGivenKey(items, 'type');
 * // Returns: {
 * //   fruit: [{ type: 'fruit', name: 'apple' }, { type: 'fruit', name: 'banana' }],
 * //   vegetable: [{ type: 'vegetable', name: 'carrot' }]
 * // }
 */

export const groupArrayOfObjectsByGivenKey = (array, groupKey) => {
  return array.reduce((accumulator, item) => {
    const groupKeyValue = item[groupKey];

    // Initialize array if it doesn't exist
    if (!accumulator[groupKeyValue]) {
      accumulator[groupKeyValue] = [];
    }

    // Add item to the group
    accumulator[groupKeyValue].push(item);

    return accumulator;
  }, {});
};

/**
 * Converts an array of objects to a map using a specified key property and value property.
 *
 * @param {Array<Object>} array - The array of objects to convert
 * @param {string} keyProperty - The property to use as the map key
 * @param {string} valueProperty - The property to use as the map value
 * @returns {Object} - An object map with keyProperty values as keys and valueProperty values as values
 * @example
 * const users = [
 *   { id: 1, name: 'Alice', email: 'alice@example.com' },
 *   { id: 2, name: 'Bob', email: 'bob@example.com' }
 * ];
 * const result = convertArrayToMapByProperties(users, 'id', 'name');
 * // returns: { 1: 'Alice', 2: 'Bob' }
 */
export const convertArrayToMapByProperties = (array, keyProperty, valueProperty) => {
  return array.reduce((acc, item) => {
    acc[item[keyProperty]] = item[valueProperty];
    return acc;
  }, {});
};

/**
 * Returns a new object with only the keys and values that are not zero.
 * @param {Object} obj - The object to filter.
 * @returns {Object} - The new object with only the keys and values that are not zero.
 */
export const getObjectWithOnlyNonZeroValuesFromGivenObject = (obj) =>
  omitBy(obj, (value) => value === 0);

export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0;
};

export const areAllValuesInGivenObjectNonNull = (obj) => {
  return Object.values(obj).every((value) => value !== null);
};

export const areAllValuesInGivenArrayNonNull = (arr) => {
  return arr.every((value) => value !== null);
};

export const getNonNullValuesFromObject = (obj) => {
  return Object.values(obj).filter((value) => value != null);
};
/*
  Checks if all values in the given array are non-null and non-empty.
  If the value is an array, it checks if the array is non-empty.
  If the value is not an array, it checks if the value is non-null.
  Returns true if all values are non-null and non-empty, false otherwise.
  Example:
  [1, 2, 3] -> true
  [1, 2, []] -> false
  [1, 2, null] -> false
  [1, 2, [1, 2, 3]] -> true
*/
export const areAllValuesInGivenArrayNonNullOrNonEmptyArray = (arr) => {
  return arr.every((value) => (Array.isArray(value) ? value.length > 0 : value !== null));
};

/**
 * Checks if any element in the array equals 'all' (case-insensitive).
 *
 * @param {string[]} arr - Array of string values to check.
 * @returns {boolean} True if any element equals 'all' (case-insensitive), false otherwise.
 */
export const doesArrayContainAll = (arr) =>
  arr.some((val) => val.toLowerCase() === ALL_IN_SMALL_CASE);

/**
 * Case-insensitive substring check. Safely handles nullish inputs (treated as empty).
 * @param {string} sourceString - The string to search within.
 * @param {string} substring - The substring to look for.
 * @returns {boolean} True if sourceString contains substring, ignoring case.
 */
export const doesStringContainSubstringCaseAgnostic = (sourceString, substring) => {
  const stringInLowerCase = (sourceString ?? '').toLowerCase();
  const substringInLowerCase = (substring ?? '').toLowerCase();

  return stringInLowerCase.includes(substringInLowerCase);
};

export const getIfAllTheGivenKeysInTheGivenObjectAreNonNull = (obj, keys) => {
  return keys.every((key) => obj[key] != null);
};

export const isKeyPresentInObject = (obj, key) => has(obj, key);

export const getIfObjectsMatchOnGivenKeys = (object1, object2, keys) =>
  keys.every((k) => object1[k] === object2[k]);

export const getFirstElementOfArray = (array) => array[0];
export const getLastElementOfArray = (array) => array[array.length - 1];
export const isLastElementInArray = (array, index) => index === array.length - 1;
export const getAllElementsOfArrayExceptFirst = (array) => array.slice(1);
export const getAllElementsOfArrayExceptLast = (array) => array.slice(0, -1);
export const getIfGivenValueIsAnEmptyArray = (givenValue) => {
  return Array.isArray(givenValue) && givenValue.length === 0;
};
export const handleAndDisableAllKeyboardEvents = (e) => {
  // Prevent all other keyboard actions:
  // - Arrow keys (Up, Down, Left, Right)
  // - Enter and Space (selection)
  // - Escape (closing dropdown)
  // - Any typing/search
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
};

export const getDisplayValueForStringOrArrayByJoiningWithComma = (value) => {
  if (value == null) {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return value;
};

/**
 * Formats a snake_case value to uppercase display format.
 * Replaces underscores with spaces and converts to uppercase.
 * Example: "cloud_provider" -> "CLOUD PROVIDER"
 *
 * @param {string} value - The field name in snake_case format
 * @returns {string} - Formatted display string in uppercase with spaces
 */
export const formatSnakeCaseToUppercaseDisplay = (value) => {
  return value.replace(/_/g, ' ').toUpperCase();
};

// Format value: replace underscores with spaces and capitalize first letter of each word
export const formatSnakeCaseToSpacesWithCapitalizedFirstLettersCase = (value) => {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export const isExpandedInputRequiredForKafka = (clusterType, rateCardId) => {
  // For the newer RCs, all cluster types need expanded input
  if (rateCardId >= RATE_CARD_ID_FOR_4_11_2024) {
    return true;
  }

  const clusterTypesForWhichExpandedInputsAreNeededForOlderRcs = [
    DEDICATED_CLUSTER_TYPE,
    ENTERPRISE_CLUSTER_TYPE,
    FREIGHT_CLUSTER_TYPE,
  ];

  return clusterTypesForWhichExpandedInputsAreNeededForOlderRcs.includes(
    clusterType?.toLowerCase()
  );
};

export const getIfGivenRowIsANewlyAddedRow = (givenRow) => {
  return givenRow?.id === DEFAULT_VALUE_FOR_DB_ROW_ID;
};

export const setDifferenceOperations = (setA, setB) => {
  // Convert to Sets if they're arrays
  const setAToUse = new Set(setA);
  const setBToUse = new Set(setB);

  // setA - setB: elements in setA but not in setB
  const aMinusB = new Set([...setAToUse].filter((x) => !setBToUse.has(x)));

  // setB - setA: elements in setB but not in setA
  const bMinusA = new Set([...setBToUse].filter((x) => !setAToUse.has(x)));

  // setA ∩ setB: elements in both setA and setB
  const aIntersectB = new Set([...setAToUse].filter((x) => setBToUse.has(x)));

  return {
    aMinusB: Array.from(aMinusB),
    bMinusA: Array.from(bMinusA),
    aIntersectB: Array.from(aIntersectB),
  };
};

/**
 * Converts an object to a Map where each value is converted to a Set.
 * Useful for converting objects with array values to Maps with Set values.
 *
 * @param obj - The object to convert
 * @returns A Map with the same keys, but values converted to Sets
 *
 * @example
 * const obj = {
 *   tier1: ['a', 'b'],
 *   tier2: ['c', 'd']
 * };
 * convertObjectOfKeyAndArrayToMapWithSetValues(obj);
 * // Returns: Map { 'tier1' => Set(['a', 'b']), 'tier2' => Set(['c', 'd']) }
 */
export const convertObjectOfKeyAndArrayToMapWithSetValues = (obj) => {
  if (!obj) {
    return new Map();
  }

  const map = new Map();
  Object.entries(obj).forEach(([key, value]) => {
    map.set(key, new Set(value));
  });

  return map;
};

export const getIfGivenValueLiesWithInTheClosedInterval = (value, min, max) => {
  // Note: This checks whether value lies in  [  min, max ] - Closed Interval
  return value >= min && value <= max;
};

export const getProvidedByTooltipTextGivenLabelText = (labelText) => {
  return PROVIDED_BY_LABEL_TEXT_TO_TOOLTIP_TEXT_MAP[labelText] ?? '';
};

/**
 * Cartesian product of arrays.
 * e.g. cartesianProduct([[1,2], ['a','b']]) => [[1,'a'],[1,'b'],[2,'a'],[2,'b']]
 */
export const getCartesianProductOfGivenArrays = (arrays) => {
  if (arrays.length === 0) {
    return [[]];
  }
  const [first, ...rest] = arrays;
  const restProduct = getCartesianProductOfGivenArrays(rest);
  return first.flatMap((firstArrayElem) =>
    restProduct.map((restArrayElem) => [firstArrayElem, ...restArrayElem])
  );
};

/**
 * Extracts the deal duration in years from an estimate object.
 *
 * @param {Object} estimate - The estimate object
 * @returns {number} The deal duration in years
 * @throws {Error} If deal_duration_years is not present in the estimate
 */
export const getDealDurationInYears = (estimate) => {
  const dealDurationInYears = estimate?.deal_duration?.deal_duration_years;
  if (!dealDurationInYears) {
    throw new Error('deal_duration_years is not present in the estimate');
  }
  return dealDurationInYears;
};

/**
 * Calculates Net New commit for renewals.
 * Net New = Current ACV (avg spend after discount) - Previous ARR
 *
 * For renewal deals, this represents the incremental value over the previous contract.
 * - Positive Net New: Customer is expanding (upsell)
 * - Negative Net New: Customer is contracting (downsell)
 * - Zero Net New: Customer is maintaining the same commitment level
 *
 * @param {number} avgSpendAfterDiscount - The current ACV (average spend after discount)
 * @param {number} previousARR - The previous Annual Run Rate
 * @returns {number} Net New commit amount (can be positive, negative, or zero)
 *
 * @example
 * // Upsell scenario
 * getNetNewCommit(200000, 150000) // Returns: 50000
 *
 * @example
 * // Downsell scenario
 * getNetNewCommit(100000, 150000) // Returns: -50000
 */
export const getNetNewCommit = (avgSpendAfterDiscount, previousARR) => {
  return avgSpendAfterDiscount - previousARR;
};

export const getRateCardVersionFromFormikValues = (formikValues) => {
  return formikValues[RATE_CARD_BACKEND_NAME];
};

// Returns the current deployment environment from window._env_.SERVICE_ENV.
// Possible values: 'local', 'devel', 'stag', 'prod'.
// Throws an error if SERVICE_ENV is set to an unrecognized value or not set.
export const getCurrentDeploymentEnvironment = () => {
  const env = window._env_?.SERVICE_ENV;

  const VALID_ENVIRONMENTS = ['local', 'devel', 'stag', 'prod'];

  if (!VALID_ENVIRONMENTS.includes(env)) {
    throw new Error(
      `Invalid SERVICE_ENV: "${env}". Expected one of: ${VALID_ENVIRONMENTS.join(', ')}`
    );
  }
  return env;
};

export const isGlobalDiscountEdited = (values, initialValues) => {
  const currentGlobalDiscount = values[GLOBAL_DISCOUNT_APPLIED_BACKEND_NAME];
  const initialGlobalDiscount = initialValues[GLOBAL_DISCOUNT_APPLIED_BACKEND_NAME];
  return Number(currentGlobalDiscount) !== Number(initialGlobalDiscount);
};

/**
 * Returns a new object with all keys trimmed of leading/trailing whitespace.
 * Note: if two different keys trim to the same string, the latter key will overwrite the former.
 * Useful when external sources (e.g. Tableau CSV exports) add extra spaces to column headers.
 */
export const trimObjectKeys = (obj) => {
  const result = {};
  for (const key of Object.keys(obj)) {
    result[key.trim()] = obj[key];
  }
  return result;
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isEmailAllowedForFeature = (allowedEmailsByEnv, email) =>
  (allowedEmailsByEnv[getCurrentDeploymentEnvironment()] || []).includes(email);

export const doesEstimateHaveAnyResourcesConfigured = (estimateData) => {
  const hasClusters = (estimateData?.cluster_summary ?? []).length > 0;
  const hasFlinkPools = (estimateData?.flink_pool_summary ?? []).length > 0;
  const hasStreamGovernance = estimateData?.stream_governance?.is_sg_configured === true;

  return hasClusters || hasFlinkPools || hasStreamGovernance;
};

export const calculateListPrice = (discountedPrice, discount) => {
  if (discountedPrice == null || discount == null || discount >= 100) {
    return null;
  }
  return discountedPrice / (1 - discount / 100);
};
